import React, { useState } from 'react';
import { X, Lock, Unlock, AlertTriangle, Shield, ShieldOff } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';

const TradingLockModal = ({ clientId, isCurrentlyLocked, onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const predefinedReasons = [
    'Подозрительная активность',
    'Верификация аккаунта',
    'По запросу клиента',
    'Нарушение правил',
    'Техническое обслуживание',
    'Риск-менеджмент',
  ];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await adminAPI.toggleTradingLock(clientId, !isCurrentlyLocked, reason || null);
      onSuccess();
    } catch (err) {
      toast.apiError(err, 'Не удалось изменить статус');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      style={{ 
        position: 'fixed', 
        inset: 0, 
        backgroundColor: 'rgba(0,0,0,0.8)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 1100 
      }} 
      onClick={onClose}
    >
      <div 
        style={{ 
          backgroundColor: COLORS.card, 
          borderRadius: '16px', 
          padding: '24px', 
          width: '100%', 
          maxWidth: '420px' 
        }} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '10px', 
              backgroundColor: isCurrentlyLocked ? `${COLORS.success}20` : `${COLORS.danger}20`, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              {isCurrentlyLocked ? (
                <ShieldOff size={20} color={COLORS.success} />
              ) : (
                <Shield size={20} color={COLORS.danger} />
              )}
            </div>
            <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
              {isCurrentlyLocked ? 'Разблокировать торговлю' : 'Заблокировать торговлю'}
            </h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Warning/Info */}
        <div style={{
          padding: '14px',
          backgroundColor: isCurrentlyLocked ? `${COLORS.success}10` : `${COLORS.danger}10`,
          borderRadius: '10px',
          marginBottom: '20px',
          border: `1px solid ${isCurrentlyLocked ? COLORS.success : COLORS.danger}30`,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            {isCurrentlyLocked ? (
              <Unlock size={20} color={COLORS.success} style={{ flexShrink: 0, marginTop: '2px' }} />
            ) : (
              <AlertTriangle size={20} color={COLORS.danger} style={{ flexShrink: 0, marginTop: '2px' }} />
            )}
            <div>
              <div style={{ color: isCurrentlyLocked ? COLORS.success : COLORS.danger, fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                {isCurrentlyLocked ? 'Разблокировка торговли' : 'Внимание!'}
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '13px', lineHeight: '1.5' }}>
                {isCurrentlyLocked 
                  ? 'После разблокировки клиент сможет открывать и закрывать позиции, совершать операции с балансом.'
                  : 'При блокировке клиент не сможет открывать и закрывать позиции. Существующие позиции останутся активными.'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Reason Selection (only for blocking) */}
        {!isCurrentlyLocked && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
              Причина (опционально)
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
              {predefinedReasons.map((r, idx) => (
                <button
                  key={idx}
                  onClick={() => setReason(r)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: reason === r ? `${COLORS.danger}20` : COLORS.background,
                    border: `1px solid ${reason === r ? COLORS.danger : COLORS.border}`,
                    borderRadius: '16px',
                    color: reason === r ? COLORS.danger : COLORS.textSecondary,
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Или введите свою причину..."
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Current Status Info */}
        <div style={{
          padding: '12px',
          backgroundColor: COLORS.background,
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Текущий статус:</span>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '4px 10px',
            backgroundColor: isCurrentlyLocked ? `${COLORS.danger}20` : `${COLORS.success}20`,
            borderRadius: '6px',
          }}>
            {isCurrentlyLocked ? <Lock size={14} color={COLORS.danger} /> : <Unlock size={14} color={COLORS.success} />}
            <span style={{ color: isCurrentlyLocked ? COLORS.danger : COLORS.success, fontSize: '12px', fontWeight: '600' }}>
              {isCurrentlyLocked ? 'Заблокирована' : 'Активна'}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: COLORS.background,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.textSecondary,
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Отмена
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: loading ? COLORS.border : (isCurrentlyLocked ? COLORS.success : COLORS.danger),
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {isCurrentlyLocked ? <Unlock size={16} /> : <Lock size={16} />}
            {loading ? 'Обработка...' : (isCurrentlyLocked ? 'Разблокировать' : 'Заблокировать')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradingLockModal;
